/**
 * OS PERFIS — em DUAS listas, e a fronteira entre elas e o assunto do arquivo.
 *
 * 1. `PERFIS_DO_GUIA` e o que a documentacao diz, literalmente e so isso.
 *    Fonte unica: guia de construcao, secao 1.4 "Perfis principais". A tabela e
 *    copia fiel: `necessidade` e a coluna "Necessidade principal" e `decisoes`
 *    e a coluna "Decisoes apoiadas", palavra por palavra. Nenhum perfil foi
 *    acrescentado A ELA, nenhum foi fundido, nenhuma coluna foi reescrita.
 *
 * 2. `PERFIS_DECIDIDOS_PELO_PROJETO` e o que o DONO decidiu acrescentar, com
 *    quem decidiu e quando escritos na propria entrada. Hoje tem uma linha: o
 *    administrador, que a secao 1.4 NAO lista — o guia para em oito perfis e
 *    nenhum deles e administrador. E o mesmo tratamento que as entradas novas
 *    de icone (`fuel` e `bell`) receberam na skill `style`: decisao registrada
 *    AO LADO do que se cita, com data e nome, e nunca dentro da citacao.
 *
 * O QUE A DOCUMENTACAO **NAO** TEM
 * A matriz de permissoes nao existe. O proprio guia a lista tres vezes como
 * pendencia — secao 19.1 "Artefatos vivos" ("Matriz de papeis e permissoes"),
 * secao 20.2 "Entregaveis de descoberta" ("Matriz de permissoes — Perfis e
 * acoes criticas mapeados") e o Apendice A, onde "[ ] Matriz de permissoes
 * desenhada" esta com a caixa NAO marcada. O guia exige que ela seja desenhada
 * ANTES do desenvolvimento. Ela nao foi.
 *
 * Por isso este arquivo para aqui. O que cada perfil pode fazer mora em
 * `proposta.ts`, separado de proposito e marcado como proposta em todo lugar
 * onde aparece — inclusive na tela. Misturar as duas coisas no mesmo modulo
 * seria a forma mais eficiente de, em tres meses, ninguem mais saber qual
 * metade veio do documento.
 */

/** Os oito da secao 1.4. Acrescentar aqui exige uma linha nova NO GUIA. */
export type IdPerfilDoGuia =
  | 'diretoria'
  | 'gestor-operacional'
  | 'planejador-manutencao'
  | 'oficina'
  | 'almoxarifado'
  | 'compras'
  | 'controladoria'
  | 'operador';

/** O que o dono decidiu acrescentar. Fora do guia, e a tela diz isso. */
export type IdPerfilDecididoPeloProjeto = 'administrador';

/** Todo perfil que o seletor oferece, venha ele de onde vier. */
export type IdPerfil = IdPerfilDoGuia | IdPerfilDecididoPeloProjeto;

export interface PerfilDoGuia {
  readonly id: IdPerfilDoGuia;
  /** Coluna "Perfil" da secao 1.4. */
  readonly nome: string;
  /** Coluna "Necessidade principal" da secao 1.4, literal. */
  readonly necessidade: string;
  /** Coluna "Decisoes apoiadas" da secao 1.4, literal. */
  readonly decisoes: string;
}

/** Os oito perfis da secao 1.4, na ordem em que o guia os lista. */
export const PERFIS_DO_GUIA: readonly PerfilDoGuia[] = [
  {
    id: 'diretoria',
    nome: 'Diretoria',
    necessidade: 'Resultado consolidado e riscos',
    decisoes: 'Investir, substituir, negociar, priorizar',
  },
  {
    id: 'gestor-operacional',
    nome: 'Gestor operacional',
    necessidade: 'Situação da frota e alocação',
    decisoes: 'Mobilizar, realocar, agir sobre ociosidade',
  },
  {
    id: 'planejador-manutencao',
    nome: 'Planejador de manutenção',
    necessidade: 'Vencimentos e recursos',
    decisoes: 'Programar parada, peças e equipe',
  },
  {
    id: 'oficina',
    nome: 'Oficina e mecânicos',
    necessidade: 'Diagnóstico, OS e histórico',
    decisoes: 'Executar, registrar e liberar',
  },
  {
    id: 'almoxarifado',
    nome: 'Almoxarifado',
    necessidade: 'Saldo, reserva e demanda',
    decisoes: 'Separar, repor e rastrear',
  },
  {
    id: 'compras',
    nome: 'Compras',
    necessidade: 'Necessidade futura e lead time',
    decisoes: 'Cotar, aprovar e comprar',
  },
  {
    id: 'controladoria',
    nome: 'Controladoria',
    necessidade: 'Custos, receitas e margens',
    decisoes: 'Analisar desvios e rentabilidade',
  },
  {
    id: 'operador',
    nome: 'Operador',
    necessidade: 'Checklist e apontamento simples',
    decisoes: 'Operar com segurança e registrar ocorrência',
  },
];

/**
 * UM PERFIL QUE NAO VEIO DO GUIA — e que por isso carrega a propria procedencia.
 *
 * Ele NAO tem `necessidade` nem `decisoes`, e a ausencia e de proposito: essas
 * duas sao COLUNAS da tabela 1.4, e nao ha linha na 1.4 de onde copia-las.
 * Preenche-las com frase escrita aqui faria um perfil decidido pelo projeto
 * parecer citacao do documento — que e exatamente a confusao que este modulo
 * foi partido em tres arquivos para evitar. Como a uniao `PerfilDisponivel` nao
 * oferece os dois campos, quem renderiza e OBRIGADO a separar os casos: a tela
 * nao tem como escrever "Necessidade principal" para o administrador nem por
 * descuido.
 */
export interface PerfilDecididoPeloProjeto {
  readonly id: IdPerfilDecididoPeloProjeto;
  readonly nome: string;
  /** O pedido com as palavras de quem pediu, entre aspas na tela. */
  readonly pedido: string;
  readonly decididoPor: string;
  /** dd/mm/aaaa. */
  readonly decididoEm: string;
  /** Por que ele nao esta — e nao pode entrar — na tabela da secao 1.4. */
  readonly foraDoGuia: string;
  /**
   * O que este perfil NAO e hoje, apesar do rotulo. Frases completas.
   *
   * Elas existem para este perfil e nao para os oito porque e aqui que a
   * ilusao sairia mais cara: "acesso a tudo" se le como controle de acesso
   * configurado, e nao ha controle de acesso nenhum. Quem acredita que o
   * administrador e um cofre com chave para de perguntar quem entra.
   */
  readonly ressalvas: readonly string[];
  /** O que a tela REGISTRA sem responder. Nao e pendencia de tela. */
  readonly perguntasAbertas: readonly string[];
}

/**
 * Perfis que o PROJETO decidiu, e o guia nao lista.
 *
 * Toda entrada aqui e uma decisao de quem assina o produto, com data e nome —
 * nao uma derivacao e nao uma leitura do documento. A regra continua valendo
 * para o proximo perfil que faltar: primeiro se procura na secao 1.4, e so
 * quando nao houver reuso honesto a decisao sobe para o dono.
 */
export const PERFIS_DECIDIDOS_PELO_PROJETO: readonly PerfilDecididoPeloProjeto[] = [
  {
    id: 'administrador',
    nome: 'Administrador',
    pedido: 'Tem que ter o usuário administrador onde tem acesso a tudo',
    decididoPor: 'Lucas Malta (dono)',
    decididoEm: '19/09/2026',
    foraDoGuia:
      'O guia de construção descreve oito perfis e nenhum administrador. Este foi decidido pelo projeto e ainda precisa entrar no guia.',
    ressalvas: [
      'Acesso a tudo quer dizer todas as permissões no papel dele. O servidor confere a permissão em cada pedido, também para o administrador: ele não passa por fora da conferência.',
      'Ação marcada "Desligada" continua desligada para ele. Conceder uma ação é diferente de ela funcionar, e as listas ao lado separam os dois casos.',
    ],
    perguntasAbertas: [
      'O administrador enxerga dado de todas as empresas, ou só da empresa dele? Fica em aberto. Hoje cada empresa só vê o próprio dado, e o banco não conhece administrador que atravesse empresas. Abrir essa passagem é decisão de segurança, e não de tela. Nada nesta versão atravessa empresa.',
    ],
  },
];

/**
 * DE ONDE O PERFIL VEIO. A tela mostra esta palavra; ninguem precisa abrir o
 * codigo para saber quais oito sao citacao e qual e decisao.
 */
export type OrigemDoPerfil = 'guia' | 'decisao-do-projeto';

export type PerfilDisponivel =
  | ({ readonly origem: 'guia' } & PerfilDoGuia)
  | ({ readonly origem: 'decisao-do-projeto' } & PerfilDecididoPeloProjeto);

/**
 * As duas listas juntas, na ordem em que a tela as oferece — e a origem e
 * ACRESCENTADA aqui, nao escrita em cada linha. As oito entradas da secao 1.4
 * continuam copia crua do guia, sem um campo sequer que o guia nao tenha.
 */
export const PERFIS_DISPONIVEIS: readonly PerfilDisponivel[] = [
  ...PERFIS_DO_GUIA.map((perfil) => ({ origem: 'guia' as const, ...perfil })),
  ...PERFIS_DECIDIDOS_PELO_PROJETO.map((perfil) => ({
    origem: 'decisao-do-projeto' as const,
    ...perfil,
  })),
];

/**
 * O valor de um `<select>` chega como `string` — sempre. O seletor de persona
 * escreve ids de perfil nas opcoes, mas quem le o valor e o navegador, e o
 * tipo nao atravessa a fronteira do DOM. Sem esta guarda, um id renomeado
 * chegaria a `perfilPorId` como string qualquer e so quebraria em tela.
 */
export function ehIdPerfil(valor: unknown): valor is IdPerfil {
  return (
    typeof valor === 'string' && PERFIS_DISPONIVEIS.some((perfil) => perfil.id === valor)
  );
}

export function perfilPorId(id: IdPerfil): PerfilDisponivel {
  const achado = PERFIS_DISPONIVEIS.find((perfil) => perfil.id === id);
  /* O tipo ja garante que o id existe; esta guarda existe para o dia em que
     alguem acrescentar um id na uniao e esquecer das duas tabelas. Erro em
     desenvolvimento e melhor que um `undefined` atravessando a tela. */
  if (!achado) {
    throw new Error(
      `Perfil "${id}" não está entre os perfis do guia de construção nem na lista de perfis decididos pelo projeto.`,
    );
  }
  return achado;
}

/**
 * O ESCOPO ALEM DO PAPEL — as dimensoes que o guia pede e que ainda nao tem
 * valor nenhum no sistema.
 *
 * Secao 7.1: "Organizacao: Empresa, filial, usuario, papel, permissao — Usuario
 * pertence a empresa e recebe escopo". Secao 14: "Autenticacao forte, RBAC e
 * escopo por empresa, filial, contrato e funcao".
 *
 * Elas aparecem na tela como dimensao EXISTENTE e SEM VALOR — texto, nao campo
 * de selecao. Um seletor de empresa que nao muda nada e pior que nenhum: quem
 * escolhe uma empresa e ve a mesma frota conclui que o isolamento esta
 * quebrado, e nao que ele ainda nao existe. O isolamento por empresa e o
 * assunto do ADR-002 e depende do banco (historia F-01).
 *
 * "FUNCAO" E "PAPEL" SAO PALAVRAS DIFERENTES, EM SECOES DIFERENTES. A 7.1 lista
 * as entidades "empresa, filial, usuario, papel, permissao"; a 14 pede escopo
 * "por empresa, filial, contrato e funcao". O guia nao diz que sao a mesma
 * coisa — quem diz e esta tela, ao tratar o perfil escolhido como a dimensao
 * "funcao". Num bloco onde tudo o mais e citacao com numero de secao ao lado,
 * essa equivalencia precisa aparecer como o que e: derivacao, escrita na
 * propria linha.
 */
export interface DimensaoDeEscopo {
  readonly nome: string;
  /** Por que ela existe, com a secao do guia de onde saiu. */
  readonly fonte: string;
  /** O que o sistema sabe dela hoje. */
  readonly situacao: string;
}

export const DIMENSOES_DE_ESCOPO: readonly DimensaoDeEscopo[] = [
  {
    nome: 'Empresa',
    fonte: 'Seção 7.1: usuário pertence a empresa e recebe escopo',
    situacao: 'Real. Vem da sua credencial e recorta tudo o que você vê.',
  },
  {
    nome: 'Filial',
    fonte: 'Seção 14: escopo por empresa, filial, contrato e função',
    situacao: 'Real quando o papel é concedido só numa filial: o banco recorta as máquinas da filial e as que estão numa obra dela (0081).',
  },
  {
    nome: 'Contrato',
    fonte: 'Seção 14: escopo por empresa, filial, contrato e função',
    situacao: 'Existe como cadastro. O escopo por contrato espera o módulo de contratos.',
  },
  {
    nome: 'Função',
    fonte: 'Seção 14: escopo por empresa, filial, contrato e função',
    situacao:
      'Sem valor como dimensão própria. O guia fala em papel e em função sem dizer se são a mesma coisa: tratar o seu perfil como a função do escopo é proposta desta tela, e não citação do guia.',
  },
];
